import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ContractItemsTableContainer from '../../features/accounting/jobsContracts/ContractItems/ContractItemsTableContainer';
import CostRevenueTableContainer from '../../features/accounting/jobsContracts/CostRevenue/CostRevenueTableContainer';
import CostToDateTableContainer from '../../features/accounting/jobsContracts/CostToDate/CostToDateTableContainer';
import JCCostEstimateTableContainer from '../../features/accounting/jobsContracts/JCCostEstimate/JCCostEstimateTableContainer';
import JCDetailTableContainer from '../../features/accounting/jobsContracts/JCDetail/JCDetailTableContainer';
import JCFilters, {
  JobContractFilters,
} from '../../features/accounting/jobsContracts/JCFilters';
import JCReport, {
  idPerJCReportType,
} from '../../features/accounting/jobsContracts/JCReport/JCReport';
import JCReportHeader from '../../features/accounting/jobsContracts/JCReport/JCReportHeader';
import JCContractAnalysis from '../../features/accounting/jobsContracts/JCContractAnalysis/JCContractAnalysisContainer';
import JCResumeTableContainer from '../../features/accounting/jobsContracts/JCResume/JCResumeTableContainer';
import checkPermissions from '../../HOCs/CheckPermissions';
import BacklogContainer from '../../features/accounting/jobsContracts/Backlog/BacklogContainer';
import JCProjectedCostTableContainer from '../../features/accounting/jobsContracts/JCProjectedCost/JCProjectedCostTableContainer';
import JCParams from '../../features/accounting/jobsContracts/JCParams';
import JCPhaseMasterContainer from '../../features/accounting/jobsContracts/PhaseMaster/JCPhaseMasterContainer';
import JCJobPhasesContainer from '../../features/accounting/jobsContracts/JCJobPhases/JCJobPhasesContainer';
import { SYSTEM_MODULES, reportsPerSection } from '@/src/utils/rolesConst';
import useProtectedReportOptions from '../../hooks/useProtectedReportOptions';
import ReportSelector from '../../components/report-selector/ReportSelector';
import FiltersMenu from '../../components/filters-menu/FiltersMenu';
import { useCompanyContext } from '../../context/CompanyContext';
import JobSetupInfoTableContainer from '../../features/accounting/jobsContracts/JobSetupInfo/JobSetupInfoTableContainer';
import { useSearchParams } from 'react-router-dom';

const JobsContracts = () => {
  const { selectedCompany } = useCompanyContext();
  const [searchParams] = useSearchParams();
  const prevSelectedCompanyRef = useRef<string | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const methods = useForm<JobContractFilters>({
    defaultValues: {
      contractState: 'active',
      extraDetails: 'hide',
    },
  });
  const { watch, resetField, getValues, setValue } = methods;
  const reportType = watch('reportType');
  const reportInfo =
    idPerJCReportType[reportType as keyof typeof idPerJCReportType];
  const [protectedReports] = useProtectedReportOptions({
    reports: SYSTEM_MODULES['accounting']['jobs & contracts'],
  });

  const JCBacklog = checkPermissions({
    sectionsToAccess: reportsPerSection['jobs & contracts'],
    Children: BacklogContainer,
  });

  const JCSummary = checkPermissions({
    sectionsToAccess: reportsPerSection['jobs & contracts'],
    Children: JCResumeTableContainer,
  });

  const Analysis = checkPermissions({
    sectionsToAccess: ['contract_analysis'],
    Children: JCContractAnalysis,
  });
  const Revenue = checkPermissions({
    sectionsToAccess: ['cost_and_revenue'],
    Children: CostRevenueTableContainer,
  });
  const Estimate = checkPermissions({
    sectionsToAccess: ['cost/estimate'],
    Children: JCCostEstimateTableContainer,
  });
  const Detail = checkPermissions({
    sectionsToAccess: ['cost_detail'],
    Children: JCDetailTableContainer,
  });
  const Cost = checkPermissions({
    sectionsToAccess: ['mtd/ytd/jtd_costs'],
    Children: CostToDateTableContainer,
  });
  const Items = checkPermissions({
    sectionsToAccess: ['contract_items_with_co_detail'],
    Children: ContractItemsTableContainer,
  });
  const ProjectedCost = checkPermissions({
    sectionsToAccess: ['projected_jc_report'],
    Children: JCProjectedCostTableContainer,
  });
  const PhaseMaster = checkPermissions({
    sectionsToAccess: ['phase_master'],
    Children: JCPhaseMasterContainer,
  });
  const JobPhases = checkPermissions({
    sectionsToAccess: ['job_phases'],
    Children: JCJobPhasesContainer,
  });
  const JobSetupProtected = checkPermissions({
    sectionsToAccess: ['job_setup_info'],
    Children: JobSetupInfoTableContainer,
  });

  const handleReportChange = () => {
    const report = getValues('reportType');
    resetField('contract');
    resetField('contractState', { defaultValue: 'active' });
    resetField('jobState', {
      defaultValue: report === 'costRevenue' ? 'all' : 'active',
    });
    resetField('endDate');
    resetField('info');
    resetField('startDate', {
      defaultValue:
        report === 'costRevenue'
          ? new Date(new Date().getFullYear(), 0, 1)
          : null,
    });
    resetField('phases');
    resetField('job');
    resetField('submittedID');
    resetField('pms');
    resetField('contracts');
    setValue('id', undefined);
    resetField('extraDetails', { defaultValue: 'hide' });
  };

  useEffect(() => {
    if (prevSelectedCompanyRef.current === null) {
      prevSelectedCompanyRef.current = selectedCompany?.id || null;
    } else if (prevSelectedCompanyRef.current !== selectedCompany?.id) {
      if (!searchParams.get('company')) {
        methods.reset();
      }
      prevSelectedCompanyRef.current = selectedCompany?.id || null;
    }
  }, [selectedCompany, methods, searchParams]);

  return (
    <div className="h-full flex flex-column">
      <FormProvider {...methods}>
        <div className="mt-6 mx-3 flex gap-4 flex-wrap justify-content-between">
          <div className="mx-auto xl:mx-0 w-full max-w-500px max-h-280px">
            <JCBacklog />
          </div>
          <div className="mx-auto xl:mx-0 w-full max-w-800px">
            <JCSummary />
          </div>
        </div>
        <div className="flex flex-column  gap-2 mx-3 mt-4">
          <div className="flex flex-row gap-2 justify-content-between">
            <ReportSelector
              protectedReports={protectedReports}
              onChange={() => {
                handleReportChange();
              }}
            />
            <div className="flex w-full justify-content-between  gap-2">
              <FiltersMenu>
                <JCFilters />
              </FiltersMenu>
              <JCReport elementRef={tableRef} />
            </div>
          </div>
        </div>
        <div
          className={`mb-3 printBackground ${
            !reportInfo?.isPortrait && 'pdfDivFitContent'
          } w-full`}
          ref={tableRef}
        >
          <JCReportHeader />
          {reportType === 'contractAnalysis' && <Analysis />}
          {reportType === 'costRevenue' && <Revenue />}
          {reportType === 'costEstimate' && <Estimate />}
          {reportType === 'costDetail' && <Detail />}
          {reportType === 'costs' && <Cost />}
          {reportType === 'itemDetails' && <Items />}
          {reportType === 'projectedJC' && <ProjectedCost />}
          {reportType === 'phaseMaster' && <PhaseMaster />}
          {reportType === 'jobPhases' && <JobPhases />}
          {reportType === 'jobSetupInfo' && <JobSetupProtected />}
        </div>
        <JCParams />
      </FormProvider>
    </div>
  );
};

export default JobsContracts;
